import { useFormik } from "formik";
import { useState } from "react";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Stack from "react-bootstrap/esm/Stack";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faBookmark, faCheck, faThumbTack } from "@fortawesome/free-solid-svg-icons";

import {
    BookmarkViewModel,
    CategoryWithCountsViewModel,
    CreateBookmarkBindingModel,
    CreateBookmarksBindingModel,
    UpdateBookmarkBindingModel,
    VideoLibraryApiClient
} from "../../apiClient";
import { getErrorMessage } from "../../helpers/errorHelper";
import { CategorySelect, CountType } from "../inputs/categorySelect";

type FormValues = {
    name?: string;
    categoryName: string;
    url: string;
}

type Props = {
    apiClient: VideoLibraryApiClient;
    bookmark?: BookmarkViewModel;
    initialUrl?: string;
    categories: CategoryWithCountsViewModel[];
    onSave: () => void;
    onCancel?: () => void;
}

export const getAddBookmarkBookmarkletHref = (origin: string = typeof window !== 'undefined' ? window.location.origin : '') =>
    `javascript:(function(){window.open('${origin}/bookmarks/add?url='+encodeURIComponent(window.location.href), '_blank');})();`;

export const BookmarkAddEditForm = ({ apiClient, bookmark, initialUrl, categories, onSave, onCancel }: Props) => {
    const [error, setError] = useState<string | null>(null);
    const isEdit = bookmark?.id !== undefined;
    const formik = useFormik<FormValues>({
        initialValues: {
            name: bookmark?.name ?? '',
            categoryName: bookmark?.categoryName ?? '',
            url: bookmark?.url ?? initialUrl ?? '',
        },
        onSubmit: async (values, helpers) => {
            setError(null);
            try {
                if (isEdit) {
                    await apiClient?.bookmarksPUT(bookmark.id, new UpdateBookmarkBindingModel({
                        name: values.name!,
                        categoryName: values.categoryName !== '' ? values.categoryName : null,
                    }));
                } else {
                    const result = await apiClient?.bookmarksPOST(new CreateBookmarksBindingModel({
                        bookmarks: [
                            new CreateBookmarkBindingModel({
                                categoryName: values.categoryName !== '' ? values.categoryName : null,
                                url: values.url,
                            }),
                        ],
                    }));

                    const failedBookmark = result?.failedBookmarks?.[0];
                    if (failedBookmark) {
                        setError(failedBookmark.reason ?? "Failed to add bookmark.");
                        helpers.setSubmitting(false);
                        return;
                    }
                }
                onSave();
            } catch (e: any) {
                console.error(e);
                setError(getErrorMessage(e));
            } finally {
                helpers.setSubmitting(false);
            }
        }
    });

    const { setFieldValue } = formik;

    return (
        <Form onSubmit={formik.handleSubmit}>
            <fieldset disabled={formik.isSubmitting}>
                <Stack direction="vertical" gap={3}>
                    {error && (
                        <Alert variant="danger" className="shadow-sm border-start border-4 border-danger">
                            <div style={{ whiteSpace: 'pre-wrap' }}>{error}</div>
                        </Alert>
                    )}

                    {!isEdit && (
                        <Form.Group controlId="bookmark-url">
                            <Form.Label>URL</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-body text-secondary">
                                    <FontAwesomeIcon icon={faLink} size="sm" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    required
                                    {...formik.getFieldProps('url')}
                                />
                            </InputGroup>
                        </Form.Group>
                    )}

                    {isEdit && (
                        <Form.Group controlId="bookmark-name">
                            <Form.Label>Name</Form.Label>
                            <InputGroup>
                                <InputGroup.Text className="bg-body text-secondary">
                                    <FontAwesomeIcon icon={faBookmark} size="sm" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Name"
                                    required
                                    {...formik.getFieldProps('name')}
                                />
                            </InputGroup>
                        </Form.Group>
                    )}

                    <Form.Group>
                        <CategorySelect
                            categories={categories}
                            name="categoryName"
                            value={formik.values.categoryName}
                            onChange={val => setFieldValue('categoryName', val)}
                            countType={CountType.Bookmarks}
                        />
                    </Form.Group>

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 pt-2">
                        <Stack direction="horizontal" gap={2}>
                            <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
                                {formik.isSubmitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                                        Save
                                    </>
                                )}
                            </Button>
                            <Button variant="secondary" onClick={() => onCancel ? onCancel() : onSave()}>
                                Cancel
                            </Button>
                        </Stack>

                        {!isEdit && (
                            <div className="d-flex align-items-center gap-2 small text-body-secondary">
                                <span>Drag to bookmarks bar:</span>
                                <a
                                    ref={(el) => {
                                        if (el) el.setAttribute('href', getAddBookmarkBookmarkletHref());
                                    }}
                                    href="#bookmarklet"
                                    onClick={(e) => e.preventDefault()}
                                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                                    title="Drag this link to your browser's bookmarks bar to quickly bookmark any page"
                                >
                                    <FontAwesomeIcon icon={faThumbTack} size="xs" />
                                    <span>+ Add Bookmark</span>
                                </a>
                            </div>
                        )}
                    </div>
                </Stack>
            </fieldset>
        </Form>
    );
};